/**
 * The parcel a "Open full form" click carries to the property create form.
 *
 * There is no property yet and so no id to look anything up by, and the URL
 * cannot hold a parcel, so the payload waits in storage under a token the new
 * tab is given. It survives a reload of that tab — dropping it on read would
 * turn a refresh into a form that can only save a property with no owner —
 * and is cleared once the property it describes exists.
 *
 * The map rewrites it under the same token whenever the owner choice changes,
 * so a form already open follows the change rather than saving the owner that
 * was chosen a minute ago.
 */

const PREFIX = 'iclimb.parcelHandoff.';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function drop(key) {
    try {
        window.localStorage.removeItem(key);
    } catch (error) {
        // Storage that refuses a write refuses a delete; nothing to recover.
    }
}

/** Tabs opened and abandoned leave their payload behind, so old ones go. */
function pruneStale() {
    const now = Date.now();

    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
        const key = window.localStorage.key(index);

        if (!key || key.indexOf(PREFIX) !== 0) continue;

        try {
            const stored = JSON.parse(window.localStorage.getItem(key));

            if (!stored || !stored.at || now - stored.at > MAX_AGE_MS) drop(key);
        } catch (error) {
            drop(key);
        }
    }
}

/**
 * @param  {string|null} token  reuse to update a payload a form already holds.
 * @returns {string|null} the token to put in the new tab's URL.
 */
export function writeParcelHandoff(payload, token) {
    token = token || Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

    try {
        pruneStale();
        window.localStorage.setItem(PREFIX + token, JSON.stringify({ ...payload, at: Date.now() }));
    } catch (error) {
        return null;
    }

    return token;
}

/** @returns {object|null} the payload, left in place for a reload. */
export function readParcelHandoff(token) {
    if (!token) return null;

    try {
        const raw = window.localStorage.getItem(PREFIX + token);

        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
}

/** The storage key a token is kept under, for listening to another tab. */
export function parcelHandoffKey(token) {
    return PREFIX + token;
}

/** Called once the property exists, so a later visit is a plain create form. */
export function clearParcelHandoff(token) {
    if (token) drop(PREFIX + token);
}
